// app/src/pages/useProjectWorkbench.ts
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { ProjectSummary } from "../types/dashboard";
import type {
    WorkbenchFlag,
    ProjectFlagsApiResponse,
    ProjectCostSummaryResponse,
    CostSuggestion,
    FlagEvidenceRow,
    FlagEvidenceApiResponse,
} from "../types/workbench";
import { api } from "../lib/apiClient";
import { useAuth } from "../auth/AuthContext";
import { hasApiBaseUrl } from "../lib/orgRuntime";

interface CostSummary {
    totalAmount: number;
    flagsWithCost: number;
    totalFlags: number;
}

interface AddServiceDraftFeeLineItem {
    label: string;
    amount: number;
}

interface AddServiceDraft {
    summaryParagraph: string;
    detailedScope: string;
    feeLineItems: AddServiceDraftFeeLineItem[];
    assumptionsAndExclusions: string;
}

interface UseProjectWorkbenchOptions {
    onDraftSuccess?: () => void;
}

export function useProjectWorkbench(
    projects: ProjectSummary[],
    options?: UseProjectWorkbenchOptions,
) {
    const { orgId } = useAuth();
    const canUseOrgScopedApi = hasApiBaseUrl && !!orgId;
    const [searchParams, setSearchParams] = useSearchParams();
    const initialProjectId =
        searchParams.get("projectId") ?? projects[0]?.id ?? null;

    const [currentProjectId, setCurrentProjectId] = useState<string | null>(
        initialProjectId,
    );
    const [flags, setFlags] = useState<WorkbenchFlag[]>([]);
    const [selectedFlagId, setSelectedFlagId] = useState<string | null>(null);
    const [isSlideoverOpen, setIsSlideoverOpen] = useState(false);

    const [notesDraft, setNotesDraft] = useState<string>("");
    const [isSavingNotes, setIsSavingNotes] = useState(false);

    const [isLoadingList, setIsLoadingList] = useState(false);
    const [isMutatingFlag, setIsMutatingFlag] = useState(false);

    const [costAmountDraft, setCostAmountDraft] = useState<string>("");
    const [costBasisDraft, setCostBasisDraft] = useState<string>("");
    const [isSavingCost, setIsSavingCost] = useState(false);

    const [costSuggestion, setCostSuggestion] = useState<CostSuggestion | null>(
        null,
    );
    const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);

    const [addServiceDraft, setAddServiceDraft] =
        useState<AddServiceDraft | null>(null);
    const [isDraftingService, setIsDraftingService] = useState(false);
    const [addServiceError, setAddServiceError] = useState<string | null>(null);

    const [costSummary, setCostSummary] = useState<CostSummary | null>(null);

    const [flagEvidence, setFlagEvidence] = useState<FlagEvidenceRow[] | null>(
        null,
    );
    const [isEvidenceLoading, setIsEvidenceLoading] = useState(false);
    const [evidenceError, setEvidenceError] = useState<string | null>(null);

    const currentProject = useMemo(() => {
        if (!currentProjectId) return null;
        return projects.find((p) => p.id === currentProjectId) ?? null;
    }, [projects, currentProjectId]);

    const selectedFlag = useMemo(
        () => flags.find((f) => f.id === selectedFlagId) ?? null,
        [flags, selectedFlagId],
    );

    useEffect(() => {
        setAddServiceDraft(null);
        setAddServiceError(null);
    }, [selectedFlagId]);

    async function loadFlags(projectId: string) {
        if (!canUseOrgScopedApi) return;

        setIsLoadingList(true);
        try {
            const json = await api.get<ProjectFlagsApiResponse>(
                `/projects/${projectId}/flags`,
            );

            if (!json.ok) {
                console.error("Flags API returned not ok");
                return;
            }

            setFlags(json.flags ?? []);
        } catch (err) {
            console.error("Error loading flags", err);
        } finally {
            setIsLoadingList(false);
        }
    }

    async function loadCostSummary(projectId: string) {
        if (!canUseOrgScopedApi) return;

        try {
            const json = await api.get<ProjectCostSummaryResponse>(
                `/projects/${projectId}/cost-summary`,
            );

            if (!json.ok) {
                console.error("Cost summary API returned not ok");
                setCostSummary(null);
                return;
            }

            setCostSummary({
                totalAmount: json.totalAmount ?? 0,
                flagsWithCost: json.flagsWithCost ?? 0,
                totalFlags: json.totalFlags ?? 0,
            });
        } catch (err) {
            console.error("Error loading project cost summary", err);
            setCostSummary(null);
        }
    }

    useEffect(() => {
        if (!currentProjectId) return;
        void loadFlags(currentProjectId);
        void loadCostSummary(currentProjectId);
    }, [currentProjectId, canUseOrgScopedApi]);

    useEffect(() => {
        if (!selectedFlagId || !canUseOrgScopedApi) {
            setFlagEvidence(null);
            setEvidenceError(null);
            return;
        }

        const loadEvidence = async () => {
            setIsEvidenceLoading(true);
            setEvidenceError(null);

            try {
                const json = await api.get<FlagEvidenceApiResponse>(
                    `/flags/${selectedFlagId}/evidence`,
                );

                if (!json.ok) {
                    throw new Error((json as any)?.error ?? "Failed to load evidence");
                }

                setFlagEvidence(json.evidence ?? []);
            } catch (err: any) {
                console.error("Error loading flag evidence", err);
                setFlagEvidence(null);
                setEvidenceError(
                    err instanceof Error ? err.message : String(err),
                );
            } finally {
                setIsEvidenceLoading(false);
            }
        };

        void loadEvidence();
    }, [selectedFlagId, canUseOrgScopedApi]);

    function handleProjectChange(projectId: string) {
        setCurrentProjectId(projectId);
        setSelectedFlagId(null);
        setIsSlideoverOpen(false);
        setNotesDraft("");
        setCostAmountDraft("");
        setCostBasisDraft("");
        setCostSuggestion(null);
        setAddServiceDraft(null);
        setAddServiceError(null);
        setFlagEvidence(null);
        setEvidenceError(null);

        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (projectId) {
                next.set("projectId", projectId);
            } else {
                next.delete("projectId");
            }
            return next;
        });
    }

    function openFlagDetails(flagId: string) {
        setSelectedFlagId(flagId);
        setIsSlideoverOpen(true);

        const flag = flags.find((f) => f.id === flagId);
        if (flag) {
            setNotesDraft(flag.notes ?? "");
            setCostAmountDraft(
                flag.costEstimate?.amount != null
                    ? String(flag.costEstimate.amount)
                    : "",
            );
            setCostBasisDraft(flag.costEstimate?.basis ?? "");
        } else {
            setNotesDraft("");
            setCostAmountDraft("");
            setCostBasisDraft("");
        }

        setCostSuggestion(null);
    }

    function closeSlideover() {
        setIsSlideoverOpen(false);
        setSelectedFlagId(null);
    }

    async function handleDismissFlag(flagId: string) {
        if (!currentProjectId || !canUseOrgScopedApi) return;

        setIsMutatingFlag(true);
        try {
            const json = await api.post<{ ok: boolean }>(
                `/flags/${flagId}/dismiss`,
                { projectId: currentProjectId },
            );

            if (!json.ok) {
                console.error("Dismiss API returned not ok");
                return;
            }

            setFlags((prev) =>
                prev.map((f) =>
                    f.id === flagId ? { ...f, status: "dismissed" } : f,
                ),
            );
        } catch (err) {
            console.error("Error dismissing flag", err);
        } finally {
            setIsMutatingFlag(false);
        }
    }

    async function handleReinstateFlag(flagId: string) {
        if (!currentProjectId || !canUseOrgScopedApi) return;

        setIsMutatingFlag(true);
        try {
            const json = await api.post<{ ok: boolean }>(
                `/flags/${flagId}/reinstate`,
                { projectId: currentProjectId },
            );

            if (!json.ok) {
                console.error("Reinstate API returned not ok");
                return;
            }

            setFlags((prev) =>
                prev.map((f) =>
                    f.id === flagId ? { ...f, status: "open" } : f,
                ),
            );
        } catch (err) {
            console.error("Error reinstating flag", err);
        } finally {
            setIsMutatingFlag(false);
        }
    }

    const handleDraftAdditionalService = async (
        primaryFlagId: string,
        selectedFlagIds?: string[],
    ) => {
        if (!currentProjectId || !canUseOrgScopedApi) return;

        // if nothing is selected, fall back to just the current flag
        const flagIds =
            selectedFlagIds && selectedFlagIds.length > 0
                ? selectedFlagIds
                : [primaryFlagId];

        setIsDraftingService(true);
        setAddServiceError(null);

        try {
            const json = await api.post(
                `/projects/${currentProjectId}/add-service-draft`,
                { flagIds },
            );

            if (!json.ok) {
                throw new Error(json.error ?? "Failed to draft additional services");
            }

            setAddServiceDraft(json.draft ?? "");
            options?.onDraftSuccess?.();
        } catch (err: any) {
            setAddServiceError(err.message ?? "Failed to draft additional services");
        } finally {
            setIsDraftingService(false);
        }
    };


    async function handleSaveNotes() {
        if (!currentProjectId || !selectedFlagId || !canUseOrgScopedApi)
            return;

        setIsSavingNotes(true);
        try {
            const json = await api.patch<{ ok: boolean }>(
                `/flags/${selectedFlagId}/notes`,
                {
                    projectId: currentProjectId,
                    notes: notesDraft.trim().length ? notesDraft.trim() : null,
                },
            );

            if (!json.ok) {
                console.error("Save notes API returned not ok");
                return;
            }

            setFlags((prev) =>
                prev.map((f) =>
                    f.id === selectedFlagId
                        ? { ...f, notes: notesDraft.trim() || null }
                        : f,
                ),
            );
        } catch (err) {
            console.error("Error saving notes", err);
        } finally {
            setIsSavingNotes(false);
        }
    }

    async function handleSaveCost() {
        if (!currentProjectId || !selectedFlagId || !canUseOrgScopedApi)
            return;

        const trimmedAmount = costAmountDraft.trim();

        // Nothing to save when the amount field is empty
        if (!trimmedAmount.length) return;

        const parsedAmount = Number(trimmedAmount);

        if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
            console.error("Invalid cost amount:", costAmountDraft);
            return;
        }

        setIsSavingCost(true);
        try {
            const json = await api.patch<{
                ok: boolean;
                flagId?: string;
                cost?: {
                    id: string;
                    amount: number;
                    basis: string | null;
                    status: "draft" | "final";
                };
            }>(
                `/flags/${selectedFlagId}/cost`,
                {
                    projectId: currentProjectId,
                    amount: parsedAmount,
                    basis: costBasisDraft.trim().length
                        ? costBasisDraft.trim()
                        : null,
                    status: "draft",
                },
            );

            if (!json.ok || !json.cost || !json.flagId) {
                console.error("Save cost API returned not ok");
                return;
            }

            const { flagId, cost } = json;

            setFlags((prev) =>
                prev.map((f) =>
                    f.id === flagId
                        ? {
                            ...f,
                            costEstimate: {
                                id: cost.id,
                                amount: cost.amount,
                                basis: cost.basis,
                                status: cost.status,
                            },
                        }
                        : f,
                ),
            );

            if (currentProjectId) {
                void loadCostSummary(currentProjectId);
            }
        } catch (err) {
            console.error("Error saving cost estimate", err);
        } finally {
            setIsSavingCost(false);
        }
    }

    async function handleAskAiCostSuggestion() {
        if (!currentProjectId || !selectedFlagId || !canUseOrgScopedApi)
            return;

        setIsLoadingSuggestion(true);
        setCostSuggestion(null);
        try {
            const json = await api.post<{
                ok: boolean;
                suggestion?: CostSuggestion;
            }>(
                `/flags/${selectedFlagId}/cost/suggest`,
                { projectId: currentProjectId },
            );

            if (!json.ok || !json.suggestion) {
                console.error("Cost suggestion API returned not ok");
                return;
            }

            setCostSuggestion(json.suggestion);
        } catch (err) {
            console.error("Error getting AI cost suggestion", err);
        } finally {
            setIsLoadingSuggestion(false);
        }
    }

    function handleUseSuggestion(which: "low" | "mid" | "high") {
        if (!costSuggestion) return;

        const amount =
            which === "low"
                ? costSuggestion.low
                : which === "mid"
                    ? costSuggestion.mid
                    : costSuggestion.high;

        if (amount != null && Number.isFinite(amount)) {
            setCostAmountDraft(String(Math.round(amount)));
        }
    }

    return {
        currentProjectId,
        currentProject,
        flags,
        selectedFlagId,
        selectedFlag,
        isSlideoverOpen,
        isLoadingList,
        notesDraft,
        setNotesDraft,
        isSavingNotes,
        isMutatingFlag,
        costAmountDraft,
        setCostAmountDraft,
        costBasisDraft,
        setCostBasisDraft,
        isSavingCost,
        costSuggestion,
        isLoadingSuggestion,
        addServiceDraft,
        isDraftingService,
        addServiceError,
        costSummary,
        flagEvidence,
        isEvidenceLoading,
        evidenceError,
        handleProjectChange,
        openFlagDetails,
        closeSlideover,
        handleDismissFlag,
        handleReinstateFlag,
        handleDraftAdditionalService,
        handleSaveNotes,
        handleSaveCost,
        handleAskAiCostSuggestion,
        handleUseSuggestion,
    };
}
